import { MultiSelect } from 'primereact/multiselect';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

const SkillsLanguagesStep = ({ data, onUpdate }) => {
    // Bank-specific skills for financial services professionals
    const BANK_SKILLS = [
        // Credit & Risk Skills
        { label: 'Credit Analysis', value: 'credit_analysis', icon: 'pi-chart-line', category: 'credit' },
        { label: 'Risk Assessment', value: 'risk_assessment', icon: 'pi-shield', category: 'credit' },
        { label: 'Credit Scoring', value: 'credit_scoring', icon: 'pi-star', category: 'credit' },
        { label: 'CIBIL Analysis', value: 'cibil_analysis', icon: 'pi-chart-bar', category: 'credit' },
        { label: 'Bureau Report Analysis', value: 'bureau_report_analysis', icon: 'pi-file', category: 'credit' },

        // Loan Processing Skills
        { label: 'Loan Processing', value: 'loan_processing', icon: 'pi-briefcase', category: 'operations' },
        { label: 'Loan Underwriting', value: 'loan_underwriting', icon: 'pi-file-edit', category: 'operations' },
        { label: 'Loan Documentation', value: 'loan_documentation', icon: 'pi-book', category: 'operations' },
        { label: 'Loan Disbursement', value: 'loan_disbursement', icon: 'pi-money-bill', category: 'operations' },
        { label: 'KYC Verification', value: 'kyc_verification', icon: 'pi-verified', category: 'operations' },

        // Sales & Relationship Skills
        { label: 'Relationship Management', value: 'relationship_management', icon: 'pi-users', category: 'sales' },
        { label: 'Customer Acquisition', value: 'customer_acquisition', icon: 'pi-user-plus', category: 'sales' },
        { label: 'Cross-selling', value: 'cross_selling', icon: 'pi-shopping-cart', category: 'sales' },
        { label: 'Lead Generation', value: 'lead_generation', icon: 'pi-search', category: 'sales' },
        { label: 'Sales Negotiation', value: 'sales_negotiation', icon: 'pi-comments', category: 'sales' },

        // Financial Analysis Skills
        { label: 'Financial Analysis', value: 'financial_analysis', icon: 'pi-calculator', category: 'finance' },
        { label: 'Income Verification', value: 'income_verification', icon: 'pi-check-square', category: 'finance' },
        { label: 'Bank Statement Analysis', value: 'bank_statement_analysis', icon: 'pi-file-pdf', category: 'finance' },
        { label: 'Financial Modeling', value: 'financial_modeling', icon: 'pi-chart-line', category: 'finance' },
        { label: 'Debt-to-Income Ratio Analysis', value: 'dti_analysis', icon: 'pi-percentage', category: 'finance' },

        // Compliance & Legal Skills
        { label: 'Banking Compliance', value: 'banking_compliance', icon: 'pi-check-circle', category: 'compliance' },
        { label: 'RBI Guidelines', value: 'rbi_guidelines', icon: 'pi-book', category: 'compliance' },
        { label: 'Anti-Money Laundering (AML)', value: 'aml', icon: 'pi-shield', category: 'compliance' },
        { label: 'Legal Documentation', value: 'legal_documentation', icon: 'pi-file-edit', category: 'compliance' },
        { label: 'FEMA Compliance', value: 'fema_compliance', icon: 'pi-globe', category: 'compliance' },

        // Product Knowledge Skills
        { label: 'Home Loan Products', value: 'home_loan_products', icon: 'pi-home', category: 'product' },
        { label: 'Personal Loan Products', value: 'personal_loan_products', icon: 'pi-user', category: 'product' },
        { label: 'Business Loan Products', value: 'business_loan_products', icon: 'pi-briefcase', category: 'product' },
        { label: 'Credit Card Products', value: 'credit_card_products', icon: 'pi-credit-card', category: 'product' },
        { label: 'Insurance Products', value: 'insurance_products', icon: 'pi-shield', category: 'product' },

        // Technical Skills
        { label: 'Core Banking Systems', value: 'core_banking_systems', icon: 'pi-server', category: 'technical' },
        { label: 'Loan Management Software', value: 'loan_management_software', icon: 'pi-desktop', category: 'technical' },
        { label: 'CRM Systems', value: 'crm_systems', icon: 'pi-database', category: 'technical' },
        { label: 'MS Excel (Advanced)', value: 'ms_excel_advanced', icon: 'pi-table', category: 'technical' },
        { label: 'Financial Software', value: 'financial_software', icon: 'pi-calculator', category: 'technical' },

        // Customer Service Skills
        { label: 'Customer Service', value: 'customer_service', icon: 'pi-comments', category: 'service' },
        { label: 'Client Communication', value: 'client_communication', icon: 'pi-phone', category: 'service' },
        { label: 'Problem Solving', value: 'problem_solving', icon: 'pi-wrench', category: 'service' },
        { label: 'Query Resolution', value: 'query_resolution', icon: 'pi-question-circle', category: 'service' },
        { label: 'Complaint Handling', value: 'complaint_handling', icon: 'pi-exclamation-triangle', category: 'service' },

        // Operations & Process Skills
        { label: 'Operations Management', value: 'operations_management', icon: 'pi-cog', category: 'operations' },
        { label: 'Process Improvement', value: 'process_improvement', icon: 'pi-chart-line', category: 'operations' },
        { label: 'Branch Operations', value: 'branch_operations', icon: 'pi-building', category: 'operations' },
        { label: 'Team Management', value: 'team_management', icon: 'pi-users', category: 'operations' },
        { label: 'Audit & Quality Control', value: 'audit_quality_control', icon: 'pi-check', category: 'operations' },
    ];

    // Languages commonly used in Indian banking sector
    const LANGUAGES = [
        // National Languages
        { label: 'English', value: 'english', icon: 'pi-globe', region: 'national' },
        { label: 'Hindi', value: 'hindi', icon: 'pi-globe', region: 'national' },

        // South Indian Languages
        { label: 'Tamil', value: 'tamil', icon: 'pi-map-marker', region: 'south' },
        { label: 'Telugu', value: 'telugu', icon: 'pi-map-marker', region: 'south' },
        { label: 'Kannada', value: 'kannada', icon: 'pi-map-marker', region: 'south' },
        { label: 'Malayalam', value: 'malayalam', icon: 'pi-map-marker', region: 'south' },

        // West Indian Languages
        { label: 'Marathi', value: 'marathi', icon: 'pi-map-marker', region: 'west' },
        { label: 'Gujarati', value: 'gujarati', icon: 'pi-map-marker', region: 'west' },

        // East Indian Languages
        { label: 'Bengali', value: 'bengali', icon: 'pi-map-marker', region: 'east' },
        { label: 'Odia', value: 'odia', icon: 'pi-map-marker', region: 'east' },
        { label: 'Assamese', value: 'assamese', icon: 'pi-map-marker', region: 'east' },

        // North Indian Languages
        { label: 'Punjabi', value: 'punjabi', icon: 'pi-map-marker', region: 'north' },
        { label: 'Urdu', value: 'urdu', icon: 'pi-map-marker', region: 'north' },

        // Other Regional Languages
        { label: 'Kashmiri', value: 'kashmiri', icon: 'pi-map-marker', region: 'north' },
        { label: 'Konkani', value: 'konkani', icon: 'pi-map-marker', region: 'west' },
        { label: 'Sindhi', value: 'sindhi', icon: 'pi-map-marker', region: 'west' },
    ];

    const handleSkillsChange = (selectedSkills) => {
        onUpdate({ skills: selectedSkills });
    };

    const handleLanguagesChange = (selectedLanguages) => {
        onUpdate({ languages: selectedLanguages });
    };

    // Template for skills dropdown items
    const skillItemTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-primary`}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    // Template for language dropdown items
    const languageItemTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-primary`}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    // Ensure skills is always an array
    const selectedSkills = Array.isArray(data.skills) ? data.skills : [];

    // Ensure languages is always an array
    const selectedLanguages = Array.isArray(data.languages) ? data.languages : [];

    return (
        <div className="grid">
            <div className="col-12">
                <h5 className="mt-0 mb-2">Skills & Languages</h5>
                <p className="text-600 mt-0 mb-4">Select your professional skills and languages you can communicate in</p>
            </div>

            {/* Skills Section */}
            <div className="col-12">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-star text-blue-500 text-xl"></i>
                        <span className="font-semibold text-lg">Professional Skills</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12">
                <Card className="shadow-2">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="skills" className="block mb-2 font-medium">
                            <i className="pi pi-briefcase mr-2 text-primary"></i>
                            Banking & Financial Skills (Select Multiple)
                        </label>

                        <MultiSelect
                            id="skills"
                            name="skills"
                            value={selectedSkills}
                            options={BANK_SKILLS}
                            onChange={(e) => handleSkillsChange(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select your professional skills..."
                            className="w-full"
                            display="chip"
                            filter
                            filterPlaceholder="Search skills..."
                            itemTemplate={skillItemTemplate}
                            maxSelectedLabels={5}
                            selectedItemsLabel="{0} skills selected"
                            emptyFilterMessage="No skills found"
                        />

                        <small className="text-500 mt-2">
                            <i className="pi pi-info-circle mr-1"></i>
                            Select all skills relevant to your banking and financial expertise. You can search and select multiple skills.
                        </small>

                        {selectedSkills.length > 0 && (
                            <div className="mt-3 p-3 bg-blue-50 border-round">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <i className="pi pi-check-circle text-blue-500"></i>
                                    <span className="font-medium text-blue-900">
                                        {selectedSkills.length} Skill{selectedSkills.length > 1 ? 's' : ''} Selected
                                    </span>
                                </div>
                                <p className="text-sm text-blue-700 m-0">
                                    Great! Your skills profile is looking strong.
                                </p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Languages Section */}
            <div className="col-12 mt-4">
                <Divider align="left">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-globe text-green-500 text-xl"></i>
                        <span className="font-semibold text-lg">Languages</span>
                    </div>
                </Divider>
            </div>

            <div className="col-12">
                <Card className="shadow-2">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="languages" className="block mb-2 font-medium">
                            <i className="pi pi-comments mr-2 text-primary"></i>
                            Languages You Speak (Select Multiple)
                        </label>

                        <MultiSelect
                            id="languages"
                            name="languages"
                            value={selectedLanguages}
                            options={LANGUAGES}
                            onChange={(e) => handleLanguagesChange(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select languages you can communicate in..."
                            className="w-full"
                            display="chip"
                            filter
                            filterPlaceholder="Search languages..."
                            itemTemplate={languageItemTemplate}
                            maxSelectedLabels={5}
                            selectedItemsLabel="{0} languages selected"
                            emptyFilterMessage="No languages found"
                        />

                        <small className="text-500 mt-2">
                            <i className="pi pi-info-circle mr-1"></i>
                            Select all languages you can effectively communicate in. Multiple language skills are valuable in the banking sector.
                        </small>

                        {selectedLanguages.length > 0 && (
                            <div className="mt-3 p-3 bg-green-50 border-round">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <i className="pi pi-check-circle text-green-500"></i>
                                    <span className="font-medium text-green-900">
                                        {selectedLanguages.length} Language{selectedLanguages.length > 1 ? 's' : ''} Selected
                                    </span>
                                </div>
                                <p className="text-sm text-green-700 m-0">
                                    Excellent! Multi-lingual capability is a strong asset for client relationships.
                                </p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Helper Tips Section */}
            <div className="col-12 mt-3">
                <div className="surface-100 border-round p-4">
                    <div className="flex align-items-start gap-3">
                        <i className="pi pi-lightbulb text-yellow-500 text-2xl"></i>
                        <div>
                            <h6 className="mt-0 mb-2 text-900">Profile Tips</h6>
                            <ul className="text-sm text-600 m-0 pl-3 line-height-3">
                                <li className="mb-2">
                                    <strong>Skills:</strong> Select all relevant banking and financial skills that match your experience. This helps in better profile matching.
                                </li>
                                <li className="mb-2">
                                    <strong>Languages:</strong> Include all languages you're comfortable communicating in professionally. This is especially important for client-facing roles.
                                </li>
                                <li className="mb-0">
                                    <strong>Search Feature:</strong> Use the search box in each dropdown to quickly find specific skills or languages.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Card */}
            {(selectedSkills.length > 0 || selectedLanguages.length > 0) && (
                <div className="col-12 mt-3">
                    <Card className="bg-primary-50 border-primary">
                        <div className="flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mt-0 mb-2 text-primary">
                                    <i className="pi pi-chart-bar mr-2"></i>
                                    Profile Completion Status
                                </h6>
                                <p className="text-sm text-700 m-0">
                                    Skills: <strong>{selectedSkills.length}</strong> selected |
                                    Languages: <strong className="ml-1">{selectedLanguages.length}</strong> selected
                                </p>
                            </div>
                            <i className="pi pi-check-circle text-primary text-4xl"></i>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default SkillsLanguagesStep;
